import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { Download, FileSpreadsheet, Search, Package, CheckCircle2, XCircle, Eye, Lock, Calendar, Send, FileBarChart } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  aprobado: { bg: "bg-green-500", title: "Aprobado" },
  pendiente: { bg: "bg-amber-400", title: "Pendiente" },
  rechazado: { bg: "bg-red-500", title: "Rechazado" },
  missing:   { bg: "bg-white border-2 border-stone-300", title: "No aportada" },
};

export default function ConsejoDashboard({ readOnlyForRole }) {
  const [matrix, setMatrix] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all|aprobado|pendiente|rechazado|missing
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDocs, setUserDocs] = useState([]);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reason, setReason] = useState("");
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [bcForm, setBcForm] = useState({ subject: "", message: "", target: "all", user_id: "" });
  const [allProducers, setAllProducers] = useState([]);

  const loadCampaigns = async () => {
    const { data } = await api.get("/campaigns");
    setCampaigns(data.campaigns);
    if (!campaign) setCampaign(data.current);
  };
  const loadMatrix = async (camp) => {
    const { data } = await api.get("/consejo/compliance-matrix", { params: { campaign: camp } });
    setMatrix(data);
  };
  useEffect(() => { loadCampaigns(); api.get("/users", { params: { role: "productor" } }).then(({ data }) => setAllProducers(data)); }, []);
  useEffect(() => { if (campaign) loadMatrix(campaign); }, [campaign]);

  const locked = matrix?.locked;

  const totals = useMemo(() => {
    if (!matrix) return { aprobado: 0, pendiente: 0, rechazado: 0, missing: 0 };
    return matrix.rows.reduce((acc, r) => ({
      aprobado: acc.aprobado + r.counts.aprobado,
      pendiente: acc.pendiente + r.counts.pendiente,
      rechazado: acc.rechazado + r.counts.rechazado,
      missing: acc.missing + r.counts.missing,
    }), { aprobado: 0, pendiente: 0, rechazado: 0, missing: 0 });
  }, [matrix]);

  const filteredRows = useMemo(() => {
    if (!matrix) return [];
    return matrix.rows.filter((r) => {
      if (search && !`${r.user.name} ${r.user.empresa} ${r.user.email} ${r.user.dni_cif}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (filter !== "all" && r.counts[filter] === 0) return false;
      return true;
    });
  }, [matrix, search, filter]);

  const openUser = async (u) => {
    setSelectedUser(u);
    const { data } = await api.get("/documents", { params: { user_id: u.id, campaign } });
    setUserDocs(data);
  };

  const updateStatus = async (status) => {
    if (!reviewDoc) return;
    try {
      await api.put(`/documents/${reviewDoc.id}/status`, { status, reason });
      toast.success(`Documento ${status}`);
      setReviewDoc(null); setReason("");
      if (selectedUser) openUser(selectedUser);
      loadMatrix(campaign);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadDoc = async (id, filename) => {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    const res = await api.get("/export/excel", { responseType: "blob", params: { campaign } });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `cumplimiento_${campaign}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportZip = async (uid, name) => {
    const res = await api.get(`/export/zip/${uid}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `docs_${name || "productor"}.zip`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExtracted = async () => {
    const res = await api.get("/export/extracted-data", { responseType: "blob", params: { campaign } });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `datos_extraidos_${campaign}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const sendBroadcast = async () => {
    if (!bcForm.message.trim()) { toast.error("Mensaje vacío"); return; }
    try {
      const { data } = await api.post("/notifications/broadcast", bcForm);
      toast.success(`Enviado a ${data.sent_to} destinatarios`);
      setBroadcastOpen(false);
      setBcForm({ subject: "", message: "", target: "all", user_id: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {!readOnlyForRole && <Header />}
      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900">Panel Consejo Regulador</h1>
            <p className="mt-1 text-stone-600 text-sm">
              Consulta, validación y control documental
              {locked && <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-medium"><Lock className="h-3 w-3" /> Campaña cerrada (solo consulta)</span>}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-stone-500" />
              <Select value={String(campaign || "")} onValueChange={(v) => setCampaign(parseInt(v))}>
                <SelectTrigger className="w-40" data-testid="select-campaign"><SelectValue placeholder="Campaña" /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c} value={String(c)}>
                      Campaña {c} {c === matrix?.current_campaign && "(actual)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setBroadcastOpen(true)} variant="outline" className="border-stone-300" data-testid="btn-broadcast">
              <Send className="h-4 w-4 mr-2" /> Notificar
            </Button>
            <Button onClick={exportExtracted} variant="outline" className="border-stone-300" data-testid="btn-export-data">
              <FileBarChart className="h-4 w-4 mr-2" /> Datos extraídos
            </Button>
            <Button onClick={exportExcel} className="bg-primary hover:bg-primary-hover" data-testid="btn-export-excel">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar Excel
            </Button>
          </div>
        </div>

        {/* KPI filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <KPIFilter label="Todos" value={matrix?.rows.length || 0} active={filter === "all"} onClick={() => setFilter("all")} testid="kpi-all" />
          <KPIFilter label="Aprobados" value={totals.aprobado} color="text-green-600" dot="bg-green-500" active={filter === "aprobado"} onClick={() => setFilter("aprobado")} testid="kpi-aprobado" />
          <KPIFilter label="Pendientes" value={totals.pendiente} color="text-amber-600" dot="bg-amber-400" active={filter === "pendiente"} onClick={() => setFilter("pendiente")} testid="kpi-pendiente" />
          <KPIFilter label="Rechazados" value={totals.rechazado} color="text-red-600" dot="bg-red-500" active={filter === "rechazado"} onClick={() => setFilter("rechazado")} testid="kpi-rechazado" />
          <KPIFilter label="No entregados" value={totals.missing} color="text-stone-600" dot="bg-white border-2 border-stone-300" active={filter === "missing"} onClick={() => setFilter("missing")} testid="kpi-missing" />
        </div>

        <Card className="p-4 rounded-xl mb-4 border-stone-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input placeholder="Buscar por nombre, empresa, DNI/CIF…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="input-search" />
          </div>
        </Card>

        {/* Compliance matrix */}
        <Card className="rounded-xl border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-stone-700 sticky left-0 bg-stone-50 z-10">Productor</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-700">Empresa</th>
                  {matrix?.types.map((t) => (
                    <th key={t.id} className="px-2 py-3 font-medium text-stone-700 text-center" title={t.name}>
                      <div className="text-[10px] uppercase tracking-wide leading-tight max-w-[80px] mx-auto">{t.name}</div>
                    </th>
                  ))}
                  <th className="text-right px-4 py-3 font-medium text-stone-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr><td colSpan={(matrix?.types.length || 0) + 3} className="text-center py-10 text-stone-500">Sin productores</td></tr>
                )}
                {filteredRows.map((r) => (
                  <tr key={r.user.id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`row-user-${r.user.id}`}>
                    <td className="px-4 py-3 sticky left-0 bg-white z-10">
                      <button onClick={() => openUser(r.user)} className="font-medium text-stone-900 hover:text-primary text-left" data-testid={`btn-open-user-${r.user.id}`}>
                        {r.user.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{r.user.empresa || "—"}</td>
                    {r.cells.map((c) => (
                      <td key={c.type_id} className="px-2 py-3 text-center">
                        <div
                          className={`h-5 w-5 rounded-full mx-auto cursor-help ${STATUS_COLORS[c.status].bg}`}
                          title={`${c.type_name} — ${STATUS_COLORS[c.status].title}`}
                          data-testid={`cell-${r.user.id}-${c.type_id}-${c.status}`}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openUser(r.user)} data-testid={`btn-view-${r.user.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => exportZip(r.user.id, r.user.empresa || r.user.name)} data-testid={`btn-zip-${r.user.id}`}>
                          <Package className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-4 flex items-center gap-4 text-xs text-stone-600 flex-wrap">
          <span className="font-medium">Leyenda:</span>
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5"><div className={`h-3 w-3 rounded-full ${v.bg}`} /> {v.title}</div>
          ))}
        </div>
      </main>

      {/* User detail */}
      <Dialog open={!!selectedUser} onOpenChange={(v) => !v && setSelectedUser(null)}>
        <DialogContent className="max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{selectedUser?.name}</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="grid grid-cols-2 gap-4 text-sm mb-4 p-4 bg-stone-50 rounded-lg">
              <Info label="Empresa" value={selectedUser.empresa} />
              <Info label="DNI/CIF" value={selectedUser.dni_cif} />
              <Info label="Email" value={selectedUser.email} />
              <Info label="Teléfono" value={selectedUser.telefono} />
              <Info label="Provincia" value={selectedUser.provincia} />
              <Info label="Dirección" value={selectedUser.direccion} />
            </div>
          )}
          <h4 className="font-heading font-semibold text-stone-900 mb-2">Documentos Campaña {campaign}</h4>
          <div className="space-y-2">
            {userDocs.length === 0 && <div className="text-stone-500 text-sm">Sin documentos subidos en esta campaña.</div>}
            {userDocs.map((d) => (
              <div key={d.id} className="p-3 border border-stone-200 rounded-lg" data-testid={`doc-row-${d.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900">{d.document_type_name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{d.filename} · {new Date(d.uploaded_at).toLocaleString("es-ES")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={d.status} />
                    <Button size="sm" variant="outline" onClick={() => downloadDoc(d.id, d.filename)} data-testid={`btn-doc-dl-${d.id}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    {!locked && (
                      <Button size="sm" onClick={() => { setReviewDoc(d); setReason(""); }} className="bg-primary hover:bg-primary-hover" data-testid={`btn-doc-review-${d.id}`}>
                        Revisar
                      </Button>
                    )}
                  </div>
                </div>
                {d.extracted_data && !d.extracted_data.error && (
                  <div className="mt-3 p-3 bg-paprika-50 border border-paprika-200 rounded-lg" data-testid={`extracted-${d.id}`}>
                    <div className="text-xs font-semibold text-paprika-700 uppercase mb-2">Datos extraídos</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      {Object.entries(d.extracted_data).filter(([k]) => k !== "error").map(([k, v]) => (
                        <div key={k}><span className="text-stone-500 capitalize">{k.replace(/_/g, " ")}: </span><span className="text-stone-900 font-medium">{v ?? "—"}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewDoc} onOpenChange={(v) => !v && setReviewDoc(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Validar: {reviewDoc?.document_type_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Textarea placeholder="Motivo (en caso de rechazo)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="textarea-reason" />
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => updateStatus("aprobado")} className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-approve">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Aprobar
              </Button>
              <Button onClick={() => updateStatus("rechazado")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="btn-reject">
                <XCircle className="h-4 w-4 mr-2" /> Rechazar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Enviar notificación</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700">Destinatarios</label>
              <Select value={bcForm.target} onValueChange={(v) => setBcForm({ ...bcForm, target: v })}>
                <SelectTrigger className="mt-1.5" data-testid="select-bc-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los productores e industrias</SelectItem>
                  <SelectItem value="agricultores">Solo agricultores</SelectItem>
                  <SelectItem value="industrias">Solo industrias</SelectItem>
                  <SelectItem value="user">Un productor específico</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500 mt-1">El Superadmin recibe siempre una copia.</p>
            </div>
            {bcForm.target === "user" && (
              <div>
                <label className="text-sm font-medium text-stone-700">Productor</label>
                <Select value={bcForm.user_id} onValueChange={(v) => setBcForm({ ...bcForm, user_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="select-bc-user"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                  <SelectContent>
                    {allProducers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} {p.empresa ? `· ${p.empresa}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700">Asunto (opcional)</label>
              <Input value={bcForm.subject} onChange={(e) => setBcForm({ ...bcForm, subject: e.target.value })} className="mt-1.5" data-testid="input-bc-subject" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Mensaje</label>
              <Textarea rows={4} value={bcForm.message} onChange={(e) => setBcForm({ ...bcForm, message: e.target.value })} className="mt-1.5" data-testid="textarea-bc-message" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancelar</Button>
              <Button onClick={sendBroadcast} className="bg-primary hover:bg-primary-hover" data-testid="btn-bc-send">
                <Send className="h-4 w-4 mr-2" /> Enviar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function KPIFilter({ label, value, color = "text-stone-900", dot, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`p-4 rounded-xl border text-left transition-all ${active ? "border-primary bg-paprika-50 shadow-sm" : "border-stone-200 bg-white hover:border-stone-300"}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-stone-500 uppercase tracking-wide">{label}</div>
        {dot && <div className={`h-3 w-3 rounded-full ${dot}`} />}
      </div>
      <div className={`text-2xl font-heading font-bold mt-1 ${color}`}>{value}</div>
    </button>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-stone-500 uppercase tracking-wide">{label}</div>
      <div className="text-stone-900 font-medium">{value || "—"}</div>
    </div>
  );
}
